import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { Alert } from '@repo/ui'
import type { AuthFactorType } from '@repo/types'
import { storage } from 'utils'
import { endVeraSpaceSession } from 'stores'
import { ROUTES } from 'routes'
import { AuthBackButton } from '../components/AuthBackButton'
import { AuthLogo } from '../components/AuthLogo'
import { authGlassCardSx } from '../components/authGlassCard'
import { TwoFactorChallenge } from '../components/TwoFactorChallenge'
import { TwoFactorEnrollmentRequired } from '../components/TwoFactorEnrollmentRequired'

/**
 * Send the failure reason back to the sign-in page.
 *
 * `source=social` is what lets SignInPage say "Google sign-in didn't complete"
 * instead of a bare "Authentication failed" — every route into this callback is
 * a redirect back from an identity provider, so a failure here is by definition
 * NOT a credential the user typed on our form.
 */
const signInWithError = (reason: string) =>
  `${ROUTES.SIGN_IN}?error=${encodeURIComponent(reason)}&source=social`

export const AuthCallbackPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  // VRG-490 §2 — the SSO-callback branch of the login redirect (Google/
  // Apple/AuthKit for an admin with a verified factor) never gets a
  // `token=` param at all; it carries `challengeRequired=true&ticket=…&
  // factorType=…` instead (see `auth.workos.controller.ts`'s `callback`
  // handler — the load-bearing constraint it documents is that NO `token=`
  // is ever added alongside either of these two outcomes).
  const [challenge, setChallenge] = useState<{ ticket: string; factorType: AuthFactorType } | null>(null)
  // VRG-490 D1 — the OTHER redirect outcome: a platform_admin with no
  // verified factor at all, nothing to challenge.
  const [enrollmentRequired, setEnrollmentRequired] = useState(false)

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get token and other params from URL
        const token = searchParams.get('token')
        const isNewUser = searchParams.get('isNewUser') === 'true'
        const errorParam = searchParams.get('error')
        const challengeRequired = searchParams.get('challengeRequired') === 'true'
        const ticket = searchParams.get('ticket')
        const factorType = searchParams.get('factorType') as AuthFactorType | null
        const enrollRequired = searchParams.get('enrollmentRequired') === 'true'

        // Check for errors from API
        if (errorParam) {
          setError(decodeURIComponent(errorParam))
          // Redirect to sign in after showing error.
          //
          // The reason travels WITH the redirect. It used to be dropped here, so a
          // failed social sign-in returned the user to a blank form with no
          // explanation — and because SignInPage attaches an un-targeted error to
          // whichever field is active, the next thing they saw was a red PASSWORD
          // box reading "Authentication failed", for a password they never typed.
          setTimeout(() => {
            navigate(signInWithError(decodeURIComponent(errorParam)), { replace: true })
          }, 3000)
          return
        }

        // VRG-490 §2 — pause here; TwoFactorChallenge completes the login
        // itself via POST /auth/2fa/challenge/verify. No auto-redirect.
        if (challengeRequired && ticket && factorType) {
          setChallenge({ ticket, factorType })
          return
        }

        // VRG-490 D1 — no auto-redirect; the explanatory screen is the
        // terminal state until the account is enrolled some other way.
        if (enrollRequired) {
          setEnrollmentRequired(true)
          return
        }

        // Validate token
        if (!token) {
          setError('Authentication failed - no token received')
          setTimeout(() => {
            navigate(signInWithError('No sign-in token was returned.'), { replace: true })
          }, 3000)
          return
        }

        // VRG-661: a new session never resumes the last one's company space. Space mode
        // is persisted in localStorage and outlives the session-cookie token, so without
        // this a platform_admin signing in landed on a brand's Settings.
        endVeraSpaceSession()

        // Store token in local storage
        storage.setItem('accessToken', token)

        // Remove refresh token if it exists (WorkOS uses single token)
        storage.removeItem('refreshToken')

        console.log('Authentication successful!', { isNewUser })

        // New users are prompted to set a password before entering the app
        if (isNewUser) {
          navigate(ROUTES.PASSWORD_SETUP, { replace: true })
          return
        }

        // VRG-339: no profile fetch and no branch here. `/` is the single decision point,
        // and it can answer questions this callback cannot — whether the first-run Home is
        // still live for this user. The old success/fallback split sent people to two
        // different places for no reason other than whether a request happened to succeed.
        navigate(ROUTES.HOME, { replace: true })
      } catch (err) {
        console.error('Callback error:', err)
        setError('An unexpected error occurred during sign in')
        setTimeout(() => {
          navigate(signInWithError('An unexpected error occurred.'), { replace: true })
        }, 3000)
      }
    }

    handleCallback()
  }, [searchParams, navigate])

  const backToSignIn = () => navigate(ROUTES.SIGN_IN, { replace: true })
  // Every state on this page pulses the mark except the two that are waiting on
  // the USER (2FA challenge, enrollment-required) and the error, which is a
  // statement, not work in flight.
  const inFlight = !challenge && !enrollmentRequired && !error

  // VRG-646 — the SAME shell SignInPage renders, so the callback's states (the
  // 2FA challenge above all, which on SSO arrives HERE rather than on sign-in)
  // look like the sign-in screen they continue. This page is routed under
  // `LayoutAuthAmbient` (App.tsx), which owns the backdrop; it used to sit
  // outside that layout and render bare `Stack`s on the plain page, which is
  // why "Verify it's you" showed a flat dark ground on platform.vera.inc.
  //
  // ⚠️ The column's `position: relative` + `zIndex: 2` are load-bearing, not
  // decoration. The ambient backdrop is `position: fixed; zIndex: 0`, and a
  // POSITIONED element paints after every in-flow block, so un-positioned
  // content here would be painted UNDER the blobs. The fixed outer shell is
  // layout only (centring, no scroll) and paints nothing.
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        px: 3,
      }}
    >
      {/* `data-auth-column` is what the ambient lobes centre on
          (SignInBackground `useLogoAnchor`), as on sign-in. */}
      <Box
        data-auth-column=""
        sx={{
          // Liquid-glass card, shared with sign-in/sign-up. FIRST, so this
          // column's own `position`/`zIndex` below win.
          ...authGlassCardSx(theme),
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: '396px',
          display: 'flex',
          flexDirection: 'column',
          gap: '60px',
          alignItems: 'center',
        }}
      >
        <AuthLogo isLoading={inFlight} />

        {/* Top-left Back, as sign-in shows on its own 2FA step. Only on the
            two states that wait for the user: the spinner and the error are
            already leaving this page. */}
        {(challenge || enrollmentRequired) && <AuthBackButton onClick={backToSignIn} />}

        <Stack spacing={2} width="100%" alignItems="stretch">
          {challenge ? (
            // VRG-490 §2 — the challenge is the terminal state for this page,
            // not a transient step on the way to one.
            <TwoFactorChallenge
              ticket={challenge.ticket}
              factorType={challenge.factorType}
              onBack={backToSignIn}
              onVerified={() => navigate(ROUTES.HOME, { replace: true })}
            />
          ) : enrollmentRequired ? (
            // VRG-490 D1 — terminal until the account is enrolled some other way.
            <TwoFactorEnrollmentRequired onBack={backToSignIn} />
          ) : error ? (
            <Stack alignItems="center" spacing={2}>
              <Alert severity="error" description={error} sx={{ width: '100%' }} />
              <Typography sx={{ fontSize: '14px', color: 'text.secondary', textAlign: 'center' }}>
                Redirecting to sign in…
              </Typography>
            </Stack>
          ) : (
            // Sign-in's own hand-off state: the pulsing wordmark above says
            // "working", plus one line naming what is happening. The 64px
            // `Loader` that used to sit here duplicated the pulse (see the
            // note on SignInPage's `isLoading` branch).
            <Stack alignItems="center" spacing={1} py={2}>
              <Typography sx={{ fontSize: '15px', color: 'text.primary', textAlign: 'center' }}>
                Completing sign in…
              </Typography>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary', textAlign: 'center' }}>
                Please wait while we set up your session
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  )
}
